<template>
  <v-abstract-menu-option :showing-dialog.sync="showingDialog">
    <template #info
      >Sellers ids {{ prefixReadable || prefix }}:
      {{ sellersIdsJoined || "Todos." }}</template
    >
    <template #input>
      <v-card>
        <v-container class="px-1 py-2">
          <v-combobox
            v-model="selectedSellers"
            append-outer-icon="send"
            chips
            clearable
            @click:append-outer="setSellersIdsAndCloseDialog"
            deletable-chips
            :disabled="!sellersWithDisplayableText.length"
            item-text="displayableText"
            item-value="id"
            :items="sellersWithDisplayableText"
            label="Filtrar por seller"
            :loading="loadingSellers"
            multiple
            no-data-text="Al parecer no pudimos cargar los sellers"
            open-on-clear
            prepend-icon="store"
            small-chips
          >
            <!-- Customize the items in the selection list -->
            <template #item="{ item }">
              <span>{{ item.name }} ({{ item.id }})</span>
            </template>
          </v-combobox>
        </v-container>
        <v-container class="px-1 py-2">
          <v-text-field
            label="Importar sellers"
            @click:append-outer="importSellers"
            append-outer-icon="send"
            v-model="sellersToImport"
            prepend-icon="store"
            clearable
          >
          </v-text-field>
        </v-container>
      </v-card>
    </template>
  </v-abstract-menu-option>
</template>

<script>
import api from "@/api";
import VAbstractMenuOption from "./AbstractMenuOption";

/**
 * An option for the settings menu to set the sellers ids to work on.
 *
 * This component expect a `namespace` to work with a module of the store
 * that has an `appSettings` object with the `sellersIds` attribute.
 *
 * It assumes that the store has a namespaced action with name
 * `setAppSettingsSellersIds` to set the ids. Is an action to dispatch
 * other actions to update the app.
 *
 * With this component is a file with a base mutation if you want to use it
 * in your store.
 */
export default {
  components: {
    VAbstractMenuOption,
  },
  props: {
    namespace: {
      type: String,
      required: true,
    },
    // If the component and its mutations are using a prefix. See mutations docs.
    prefix: {
      type: String,
      default: "",
    },
    // An optional readable version of the prefix to show in the component. If none is given
    // it will use the prefix raw value.
    prefixReadable: {
      type: String,
      default: "",
    },
  },
  data: () => ({
    loadingSellers: false,
    sellers: [],
    selectedSellers: [],
    showingDialog: false,
    sellersToImport: "",
  }),
  computed: {
    /**
     * Get the app settings from the namespaced store module.
     */
    appSettings() {
      return this.$store.state[this.namespace].appSettings;
    },
    /**
     * Get the ids from the store and join them with a ", " string.
     */
    sellersIdsJoined() {
      return this.appSettings[this.settingKey].join(", ");
    },
    /**
     * Add a `displayableText` to the sellers object to provide them to the combobox.
     */
    sellersWithDisplayableText() {
      return this.sellers.map((seller) => ({
        ...seller,
        displayableText: `${seller.name} (${seller.id})`,
      }));
    },
    /**
     * Compute the setting key and add the prefix if we are using one.
     */
    settingKey() {
      return this.prefix
        ? `${this.prefix.charAt(0).toLowerCase() +
            this.prefix.slice(1)}SellersIds`
        : "sellersIds";
    },
  },
  methods: {
    /**
     * Get the sellers from the backend.
     * The `loadingSellers` flag is true while this method is getting
     * the sellers.
     */
    getSellers() {
      this.loadingSellers = true;
      return api.global.sellers
        .getAll()
        .then((sellers) => {
          this.sellers = sellers;
        })
        .catch(() =>
          this.$notify.error(
            "No pudimos obtener los sellers para app setting sellers ids"
          )
        )
        .then(() => {
          this.loadingSellers = false;
        });
    },
    /**
     * Set the sellers ids in the store by dispatching the action with
     * the namespace.
     */
    setSellersIdsAndCloseDialog() {
      this.$store.dispatch(
        `${this.namespace}/setAppSettings${this.settingKey
          .charAt(0)
          .toUpperCase() + this.settingKey.slice(1)}`,
        this.selectedSellers.map((seller) => seller.id)
      );
      this.showingDialog = false;
    },
    /**
     * Take the ids of the sellers from the store and set them as the selected ones.
     */
    setSelectedSellersBasedOnTheStore() {
      this.selectedSellers = this.appSettings[this.settingKey].map((id) => {
        return this.sellersWithDisplayableText.find(
          (seller) => seller.id === id
        );
      });
    },

    /**
     * Set the selected sellers from the import sellers input
     */
    importSellers() {
      const rawSellersIds = this.sellersToImport;
      const sellersIdsSeparatedByCommas = rawSellersIds.split(",");
      const sellersIdsToFilter = sellersIdsSeparatedByCommas
        .map((seller) => {
          return parseInt(seller.trim());
        })
        .filter((seller) => seller !== "");
      const sellers = this.sellersWithDisplayableText.filter((seller) =>
        sellersIdsToFilter.includes(seller.id)
      );
      this.selectedSellers = sellers;
    },
  },
  /**
   * Get the sellers when the component is created.
   */
  created() {
    this.getSellers();
  },
  watch: {
    /**
     * When the component is shown check that the selected sellers here
     * are the same as the ones in the store.
     */
    showingDialog() {
      if (this.showingDialog) {
        this.setSelectedSellersBasedOnTheStore();
      }
    },
  },
};
</script>
